import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { CourseGroupOption, useCourseGroupsOptions } from "api/hooks";
import {
  PaziaFormButton,
  PaziaInput,
  PaziaSelect,
  PaziaFileDropzone,
} from "components/atoms";
import { CourseApi } from "api";
import { FormProvider, useForm } from "react-hook-form";

import { SLUG_MESSAGE, SLUG_PATTERN, toSlug } from "utils/slug";
import { useToaster } from "utils";
import { PaziaBox } from "components/atoms";
import { CourseStatus } from "pages/CourseManage/CourseStatus";
import { useCategoriesOptions } from "api/categories";

interface BaseInformationStepProps {
  course?: CourseApi;
  onSubmit: (data: BaseInformationFormSchema) => Promise<void>;
}
export const BaseInformationStep = ({ course, onSubmit }: BaseInformationStepProps): JSX.Element => {
  const { showWarning } = useToaster();
  const { loading, options: categories } = useCategoriesOptions();
  const methods = useForm({
    resolver: yupResolver(schema),
    context: { savedSlug: course?.data.slug },
    defaultValues: {
      group: course?.group.id,
      categoryId: course ? course.group.category.id : undefined,
      newGroup: "",
      courseName: course ? course.data.name : "",
      slug: course ? course.data.slug : "",
      edition: course?.data.edition,
      variant: course?.data.variant,
      mainImage: course ? course.data.image : "",
    },
  });

  const { handleSubmit, setValue, watch, getValues } = methods;
  const newGroup = watch(COURSE_BASE_FORM_NAMES.NEWGROUP);
  const categoryWatch = watch(COURSE_BASE_FORM_NAMES.CATEGORY_ID);
  const groupWatch = watch(COURSE_BASE_FORM_NAMES.GROUP);
  const createNewGroup = watch(COURSE_BASE_FORM_NAMES.GROUP) == -1;
  const { loading: loadingGroups, options: groups } = useCourseGroupsOptions(true);

  const onGroupChange = (groupOption: CourseGroupOption) => {
    if (course?.data.id || !groupOption) return;
    // let edition;
    if (groupOption.value == -1) {
      // edition = 1;
      // setValue(COURSE_BASE_FORM_NAMES.COURSENAME, "");
    } else {
      const selectedGroup = groups.find((x) => x.value == groupOption.value);
      if (!selectedGroup) return;
      // edition = (selectedGroup?.group?.maxEdition || 0) + 1;
      // const name = `${selectedGroup?.group?.name} Edycja ${edition}`;
      // setValue(COURSE_BASE_FORM_NAMES.COURSENAME, name);
      // setValue(COURSE_BASE_FORM_NAMES.SLUG, toSlug(name));
    }
    // setValue(COURSE_BASE_FORM_NAMES.EDITION, edition); TODO
  };

  const onEditionChange = (editionValue: React.ChangeEvent<HTMLInputElement>) => {
    if (course?.data.id) return;
    const selectedGroup = groups.find((x) => x.value == groupWatch);
    if (!selectedGroup) return;
    const groupName = selectedGroup.value == -1 ? newGroup : selectedGroup.group?.name;
    // The edition is free text that already carries the word ("Edycja Zima 2026"), so it is not added again
    const name = `${groupName} ${editionValue.target.value}`;
    setValue(COURSE_BASE_FORM_NAMES.COURSENAME, name);
    setValue(COURSE_BASE_FORM_NAMES.SLUG, toSlug(name));
  };

  const onNewGroupChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (course?.data.id) return;
    setValue(COURSE_BASE_FORM_NAMES.COURSENAME, event.target.value);
    setValue(COURSE_BASE_FORM_NAMES.SLUG, toSlug(event.target.value));
  };

  const onFileToLarge = () => {
    showWarning("Zdjęcie jest za duże. Maksymalny rozmiar to 5MB.");
  };
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="pt-4">
        <div className="lg:grid lg:grid-cols-2 lg:gap-4">
          <PaziaBox className="col-span-2 flex justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Dane podstawowe</h2>
            </div>
            <div>
              <PaziaFormButton title="Zapisz" type="success" />
            </div>
          </PaziaBox>
          <PaziaBox className="flex flex-col gap-4">
            <PaziaSelect
              label="Kategoria"
              name={COURSE_BASE_FORM_NAMES.CATEGORY_ID}
              options={categories}
              required
              disabled={loading}
            />

            <PaziaSelect
              label="Grupa"
              name={COURSE_BASE_FORM_NAMES.GROUP}
              required
              isLoading={loadingGroups}
              options={groups.filter((x) => x.group?.category.id == categoryWatch || x.value == -1)}
              onChange={onGroupChange}
            />
            {createNewGroup && (
              <PaziaInput
                required
                label="Nazwa grupy"
                name={COURSE_BASE_FORM_NAMES.NEWGROUP}
                onChange={onNewGroupChange}
              />
            )}
            <PaziaInput label="Nazwa" name={COURSE_BASE_FORM_NAMES.COURSENAME} required className="w-full" />
            <div className="flex gap-2">
              <PaziaInput
                label="Edycja"
                name={COURSE_BASE_FORM_NAMES.EDITION}
                required
                onChange={onEditionChange}
                className="w-1/2"
              />
              <PaziaInput label="Wariant" name={COURSE_BASE_FORM_NAMES.VARIANT} className="w-1/2" />
            </div>
            <PaziaInput
              label="Slug"
              name={COURSE_BASE_FORM_NAMES.SLUG}
              required
              tooltip="Slug jest fragmentem ścieżki URL pod którą użytkownik może wyszukać kurs."
            />

            {course && <CourseStatus courseId={course.data.id} />}
          </PaziaBox>
          <PaziaBox className="mt-4 lg:mt-0">
            <PaziaFileDropzone
              name={COURSE_BASE_FORM_NAMES.MAIN_IMAGE}
              label="Zdjęcie główne"
              required
              type="image"
              onFileToLarge={onFileToLarge}
            />
          </PaziaBox>
        </div>
      </form>
    </FormProvider>
  );
};

const COURSE_BASE_FORM_NAMES = {
  CATEGORY_ID: "categoryId",
  GROUP: "group",
  NEWGROUP: "newGroup",
  COURSENAME: "courseName",
  EDITION: "edition",
  VARIANT: "variant",
  SLUG: "slug",
  MAIN_IMAGE: "mainImage",
} as const;

const schema = yup
  .object({
    [COURSE_BASE_FORM_NAMES.CATEGORY_ID]: yup.number().nullable().required(),
    [COURSE_BASE_FORM_NAMES.GROUP]: yup.number().required(),
    [COURSE_BASE_FORM_NAMES.NEWGROUP]: yup.string().when(COURSE_BASE_FORM_NAMES.GROUP, {
      is: -1,
      then: (shema) => shema.required(),
    }),
    [COURSE_BASE_FORM_NAMES.COURSENAME]: yup.string().required(),
    [COURSE_BASE_FORM_NAMES.EDITION]: yup.string().required(),
    [COURSE_BASE_FORM_NAMES.VARIANT]: yup.string().nullable(),
    [COURSE_BASE_FORM_NAMES.SLUG]: yup
      .string()
      .required()
      // Same rule as the API; a slug saved before the rule existed may be kept unchanged
      .test("slug-format", SLUG_MESSAGE, (value, ctx) =>
        Boolean(value && (value === ctx.options.context?.savedSlug || SLUG_PATTERN.test(value))),
      ),
    [COURSE_BASE_FORM_NAMES.MAIN_IMAGE]: yup
      .mixed()
      .required()
      .test(COURSE_BASE_FORM_NAMES.MAIN_IMAGE, "Zdjęcie jest wymagane.", (value) => {
        if (value === "") return false;
        return true;
      }),
  })
  .required();

export type BaseInformationFormSchema = yup.InferType<typeof schema>;
